import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { 
  User, 
  Calendar, 
  DollarSign, 
  Mail, 
  Phone, 
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Save,
  Instagram,
  Youtube,
  Edit,
  Eye,
  MessageSquare,
  Upload,
  ImageIcon,
  Plus,
  Trash2,
  Package
} from "lucide-react";
import { FaTiktok } from "react-icons/fa";

interface PackageItem {
  id?: string;
  name: string;
  description: string;
  duration_seconds: number;
  price: number;
}

const CATEGORIES = [
  { value: "lifestyle", labelEn: "Lifestyle", labelAr: "نمط الحياة" },
  { value: "fashion", labelEn: "Fashion", labelAr: "الموضة" },
  { value: "beauty", labelEn: "Beauty", labelAr: "الجمال" },
  { value: "fitness", labelEn: "Fitness", labelAr: "اللياقة" },
  { value: "tech", labelEn: "Technology", labelAr: "التقنية" },
  { value: "food", labelEn: "Food", labelAr: "الطعام" },
  { value: "travel", labelEn: "Travel", labelAr: "السفر" },
  { value: "gaming", labelEn: "Gaming", labelAr: "الألعاب" },
  { value: "business", labelEn: "Business", labelAr: "الأعمال" },
  { value: "entertainment", labelEn: "Entertainment", labelAr: "الترفيه" },
];

interface InfluencerProfile {
  id: string;
  name: string;
  category: string;
  description: string | null;
  followers: string;
  price: number;
  image: string | null;
  rating: number;
  expertise: string[];
  packages: PackageItem[];
  social_links: {
    instagram?: string;
    youtube?: string;
    tiktok?: string;
  };
  contact_info: {
    email?: string;
    phone?: string;
  };
  approval_status: 'pending' | 'approved' | 'rejected';
  availability: string;
}

interface Booking {
  id: string;
  requester_name: string;
  requester_email: string;
  requester_phone: string | null;
  company_name: string | null;
  campaign_type: string;
  campaign_description: string | null;
  budget: number | null;
  preferred_start_date: string | null;
  preferred_end_date: string | null;
  status: 'pending' | 'accepted' | 'rejected' | 'completed' | 'cancelled';
  influencer_notes: string | null;
  created_at: string;
}

const InfluencerDashboard = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isRTL = language === 'ar';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<InfluencerProfile | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [editedProfile, setEditedProfile] = useState<Partial<InfluencerProfile>>({});
  const [editedPackages, setEditedPackages] = useState<PackageItem[]>([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  
  const [bookingDialog, setBookingDialog] = useState<{ open: boolean; booking: Booking | null }>({
    open: false,
    booking: null
  });
  const [bookingNotes, setBookingNotes] = useState("");
  const [updatingBooking, setUpdatingBooking] = useState(false);
  const [deletingProfile, setDeletingProfile] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);
    
    try {
      // Fetch influencer profile
      const { data: profileData, error: profileError } = await supabase
        .from('influencers')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError) throw profileError;

      if (!profileData) {
        navigate('/influencer-signup');
        return;
      }

      const typedProfile: InfluencerProfile = {
        ...profileData,
        social_links: (profileData.social_links || {}) as InfluencerProfile['social_links'],
        contact_info: (profileData.contact_info || {}) as InfluencerProfile['contact_info'],
        expertise: (profileData.expertise || []) as string[],
        packages: (Array.isArray(profileData.packages) ? profileData.packages : []) as unknown as PackageItem[],
        approval_status: (profileData.approval_status || 'pending') as InfluencerProfile['approval_status'],
      };

      setProfile(typedProfile);
      setEditedProfile(typedProfile);
      setEditedPackages(typedProfile.packages.map((p, i) => ({ ...p, id: p.id || `pkg-${i}` })));

      // Fetch bookings
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('influencer_bookings')
        .select('*')
        .eq('influencer_id', profileData.id)
        .order('created_at', { ascending: false });

      if (bookingsError) throw bookingsError;
      
      const typedBookings = (bookingsData || []).map(b => ({
        ...b,
        status: b.status as Booking['status']
      }));
      setBookings(typedBookings);

    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "فشل في جلب البيانات" : "Failed to fetch data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "يرجى اختيار ملف صورة" : "Please select an image file",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "حجم الصورة يجب أن يكون أقل من 5 ميجابايت" : "Image size must be less than 5MB",
        variant: "destructive",
      });
      return;
    }

    setUploadingImage(true);

    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/profile.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('influencer-images')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('influencer-images')
        .getPublicUrl(fileName);

      setEditedProfile(prev => ({ ...prev, image: publicUrl }));

      toast({
        title: isRTL ? "تم الرفع" : "Uploaded",
        description: isRTL ? "تم رفع الصورة بنجاح" : "Image uploaded successfully",
      });
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "فشل في رفع الصورة" : "Failed to upload image",
        variant: "destructive",
      });
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profile) return;
    
    // Validate packages
    const validPackages = editedPackages.filter(p => p.name && p.price > 0);
    if (validPackages.length === 0) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "يجب إضافة باقة واحدة على الأقل" : "At least one package is required",
        variant: "destructive",
      });
      return;
    }
    
    setSaving(true);

    try {
      const { error } = await supabase
        .from('influencers')
        .update({
          name: editedProfile.name,
          category: editedProfile.category,
          description: editedProfile.description,
          followers: editedProfile.followers,
          price: Math.min(...validPackages.map(p => p.price)),
          packages: validPackages.map(({ id, ...rest }) => rest),
          image: editedProfile.image,
          availability: editedProfile.availability,
          social_links: editedProfile.social_links,
          contact_info: editedProfile.contact_info,
        })
        .eq('id', profile.id);

      if (error) throw error;

      setProfile({ ...profile, ...editedProfile } as InfluencerProfile);
      setEditMode(false);
      toast({
        title: isRTL ? "تم الحفظ" : "Saved",
        description: isRTL ? "تم تحديث ملفك الشخصي" : "Your profile has been updated",
      });
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "فشل في حفظ التغييرات" : "Failed to save changes",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateBookingStatus = async (status: 'accepted' | 'rejected' | 'completed') => {
    if (!bookingDialog.booking) return;
    setUpdatingBooking(true);

    try {
      const { error } = await supabase
        .from('influencer_bookings')
        .update({
          status,
          influencer_notes: status === 'completed' ? bookingDialog.booking.influencer_notes : (bookingNotes || null),
        })
        .eq('id', bookingDialog.booking.id);

      if (error) throw error;

      // Send completion notification email if marking as completed
      if (status === 'completed' && profile) {
        try {
          const dashboardUrl = `${window.location.origin}/dashboard`;
          await supabase.functions.invoke('send-booking-completion', {
            body: {
              requesterName: bookingDialog.booking.requester_name,
              requesterEmail: bookingDialog.booking.requester_email,
              influencerName: profile.name,
              influencerId: profile.id,
              campaignType: bookingDialog.booking.campaign_type,
              dashboardUrl,
            },
          });
          console.log('Completion notification email sent');
        } catch (emailError) {
          console.error('Failed to send completion email:', emailError);
          // Don't fail the status update if email fails
        }
      }

      setBookings(prev => prev.map(b => 
        b.id === bookingDialog.booking!.id 
          ? { ...b, status, influencer_notes: status === 'completed' ? b.influencer_notes : bookingNotes }
          : b
      ));

      const statusMessages = {
        accepted: isRTL ? "تم القبول" : "Accepted",
        rejected: isRTL ? "تم الرفض" : "Rejected",
        completed: isRTL ? "تم الإكمال" : "Completed"
      };

      toast({
        title: statusMessages[status],
        description: isRTL ? "تم تحديث حالة الطلب" : "Booking status updated",
      });

      setBookingDialog({ open: false, booking: null });
      setBookingNotes("");
    } catch (error) {
      console.error('Error updating booking:', error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "فشل في تحديث الطلب" : "Failed to update booking",
        variant: "destructive",
      });
    } finally {
      setUpdatingBooking(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!profile) return;
    
    setDeletingProfile(true);
    try {
      const { error } = await supabase
        .from('influencers')
        .delete()
        .eq('id', profile.id);

      if (error) throw error;

      toast({
        title: isRTL ? "تم الحذف" : "Deleted",
        description: isRTL ? "تم حذف ملف المؤثر بنجاح" : "Influencer profile deleted successfully",
      });
      
      navigate('/client-dashboard');
    } catch (error) {
      console.error('Error deleting profile:', error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "فشل في حذف الملف" : "Failed to delete profile",
        variant: "destructive",
      });
    } finally {
      setDeletingProfile(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const pendingBookings = bookings.filter(b => b.status === 'pending').length;

  return (
    <div className={`container mx-auto px-4 py-8 ${isRTL ? 'text-right' : 'text-left'}`}>
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold">{isRTL ? "لوحة تحكم المؤثر" : "Influencer Dashboard"}</h1>
          <p className="text-muted-foreground">{isRTL ? "إدارة ملفك الشخصي وطلبات الحجز" : "Manage your profile and booking requests"}</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={
            profile.approval_status === 'approved' ? 'default' :
            profile.approval_status === 'pending' ? 'secondary' : 'destructive'
          } className="text-sm">
            {profile.approval_status === 'approved' 
              ? (isRTL ? "موافق عليه" : "Approved")
              : profile.approval_status === 'pending'
              ? (isRTL ? "قيد المراجعة" : "Pending Approval")
              : (isRTL ? "مرفوض" : "Rejected")}
          </Badge>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                disabled={deletingProfile}
              >
                {deletingProfile ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                {isRTL ? "حذف الملف" : "Delete Profile"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {isRTL ? "حذف ملف المؤثر" : "Delete Influencer Profile"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {isRTL 
                    ? "هل أنت متأكد من حذف ملف المؤثر الخاص بك؟ سيتم حذف جميع بيانات ملفك الشخصي وسجل الحجوزات. لا يمكن التراجع عن هذا الإجراء."
                    : "Are you sure you want to delete your influencer profile? All your profile data and booking history will be deleted. This action cannot be undone."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{isRTL ? "إلغاء" : "Cancel"}</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDeleteProfile}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {isRTL ? "حذف" : "Delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {profile.approval_status !== 'approved' && (
        <Card className="mb-6 border-yellow-500/50 bg-yellow-500/5">
          <CardContent className="pt-6">
            <p className="text-sm">
              {profile.approval_status === 'pending'
                ? (isRTL ? "ملفك الشخصي قيد المراجعة. ستتمكن من استقبال طلبات الحجز بعد الموافقة." : "Your profile is under review. You'll be able to receive bookings once approved.")
                : (isRTL ? "تم رفض ملفك الشخصي. يرجى التواصل مع الدعم لمزيد من المعلومات." : "Your profile was rejected. Please contact support for more information.")}
            </p>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile">
            <User className="h-4 w-4 mr-2" />
            {isRTL ? "الملف الشخصي" : "Profile"}
          </TabsTrigger>
          <TabsTrigger value="bookings" className="relative">
            <Calendar className="h-4 w-4 mr-2" />
            {isRTL ? "طلبات الحجز" : "Bookings"}
            {pendingBookings > 0 && (
              <Badge variant="destructive" className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                {pendingBookings}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{isRTL ? "معلومات الملف الشخصي" : "Profile Information"}</CardTitle>
                <CardDescription>
                  {isRTL ? "قم بتحديث معلوماتك المعروضة للعلامات التجارية" : "Update your information displayed to brands"}
                </CardDescription>
              </div>
              {!editMode ? (
                <Button variant="outline" onClick={() => setEditMode(true)}>
                  <Edit className="h-4 w-4 mr-2" />
                  {isRTL ? "تعديل" : "Edit"}
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => {
                    setEditMode(false);
                    setEditedProfile(profile);
                  }}>
                    {isRTL ? "إلغاء" : "Cancel"}
                  </Button>
                  <Button onClick={handleSaveProfile} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    {isRTL ? "حفظ" : "Save"}
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                <Avatar className="h-20 w-20 border-2 border-dashed border-muted-foreground/25">
                  <AvatarImage src={imagePreview || (editMode ? editedProfile.image : profile.image) || undefined} />
                  <AvatarFallback>
                    {editMode ? <ImageIcon className="h-8 w-8 text-muted-foreground" /> : profile.name.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                {editMode && (
                  <div className="flex-1">
                    <label htmlFor="dashboard-image-upload" className="cursor-pointer">
                      <div className="flex items-center justify-center gap-2 px-4 py-2 border border-dashed border-muted-foreground/25 rounded-lg hover:bg-muted/50 transition-colors">
                        {uploadingImage ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="text-sm">{isRTL ? "جاري الرفع..." : "Uploading..."}</span>
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4" />
                            <span className="text-sm">{isRTL ? "تغيير الصورة" : "Change Image"}</span>
                          </>
                        )}
                      </div>
                      <input
                        id="dashboard-image-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      {isRTL ? "الحد الأقصى 5 ميجابايت" : "Max 5MB"}
                    </p>
                  </div>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>{isRTL ? "الاسم" : "Name"}</Label>
                  {editMode ? (
                    <Input
                      value={editedProfile.name || ""}
                      onChange={(e) => setEditedProfile(prev => ({ ...prev, name: e.target.value }))}
                    />
                  ) : (
                    <p className="text-lg font-medium">{profile.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>{isRTL ? "الفئة" : "Category"}</Label>
                  {editMode ? (
                    <Select 
                      value={editedProfile.category || ""} 
                      onValueChange={(value) => setEditedProfile(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {isRTL ? cat.labelAr : cat.labelEn}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-lg capitalize">{profile.category}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>{isRTL ? "عدد المتابعين" : "Followers"}</Label>
                  {editMode ? (
                    <Input
                      value={editedProfile.followers || ""}
                      onChange={(e) => setEditedProfile(prev => ({ ...prev, followers: e.target.value }))}
                    />
                  ) : (
                    <p className="text-lg">{profile.followers}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>{isRTL ? "سعر الحملة (ريال)" : "Campaign Price (SAR)"}</Label>
                  {editMode ? (
                    <Input
                      type="number"
                      value={editedProfile.price || ""}
                      onChange={(e) => setEditedProfile(prev => ({ ...prev, price: parseFloat(e.target.value) }))}
                    />
                  ) : (
                    <p className="text-lg">{profile.price.toLocaleString()} SAR</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>{isRTL ? "الحالة" : "Availability"}</Label>
                  {editMode ? (
                    <Select 
                      value={editedProfile.availability || ""} 
                      onValueChange={(value) => setEditedProfile(prev => ({ ...prev, availability: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Available now">{isRTL ? "متاح الآن" : "Available now"}</SelectItem>
                        <SelectItem value="Available next week">{isRTL ? "متاح الأسبوع القادم" : "Available next week"}</SelectItem>
                        <SelectItem value="Busy">{isRTL ? "مشغول" : "Busy"}</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-lg">{profile.availability}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>{isRTL ? "نبذة عنك" : "About"}</Label>
                {editMode ? (
                  <Textarea
                    value={editedProfile.description || ""}
                    onChange={(e) => setEditedProfile(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                  />
                ) : (
                  <p className="text-muted-foreground">{profile.description || (isRTL ? "لا يوجد وصف" : "No description")}</p>
                )}
              </div>

              {/* Social Links */}
              <div className="space-y-4">
                <Label>{isRTL ? "روابط التواصل الاجتماعي" : "Social Links"}</Label>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="flex items-center gap-2">
                    <Instagram className="h-5 w-5 text-pink-500" />
                    {editMode ? (
                      <Input
                        value={editedProfile.social_links?.instagram || ""}
                        onChange={(e) => setEditedProfile(prev => ({ 
                          ...prev, 
                          social_links: { ...prev.social_links, instagram: e.target.value }
                        }))}
                        placeholder="Instagram URL"
                        className="flex-1"
                      />
                    ) : (
                      <span className="text-sm truncate">{profile.social_links?.instagram || "-"}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Youtube className="h-5 w-5 text-red-500" />
                    {editMode ? (
                      <Input
                        value={editedProfile.social_links?.youtube || ""}
                        onChange={(e) => setEditedProfile(prev => ({ 
                          ...prev, 
                          social_links: { ...prev.social_links, youtube: e.target.value }
                        }))}
                        placeholder="YouTube URL"
                        className="flex-1"
                      />
                    ) : (
                      <span className="text-sm truncate">{profile.social_links?.youtube || "-"}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <FaTiktok className="h-5 w-5" />
                    {editMode ? (
                      <Input
                        value={editedProfile.social_links?.tiktok || ""}
                        onChange={(e) => setEditedProfile(prev => ({ 
                          ...prev, 
                          social_links: { ...prev.social_links, tiktok: e.target.value }
                        }))}
                        placeholder="TikTok URL"
                        className="flex-1"
                      />
                    ) : (
                      <span className="text-sm truncate">{profile.social_links?.tiktok || "-"}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Packages */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">{isRTL ? "باقات التسعير" : "Pricing Packages"}</Label>
                  {editMode && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditedPackages(prev => [...prev, { 
                        id: crypto.randomUUID(), 
                        name: "", 
                        description: "", 
                        duration_seconds: 15, 
                        price: 0 
                      }])}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      {isRTL ? "إضافة" : "Add"}
                    </Button>
                  )}
                </div>

                {editMode ? (
                  <div className="space-y-3">
                    {editedPackages.map((pkg, index) => (
                      <Card key={pkg.id} className="p-3">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-muted-foreground">
                              {isRTL ? `الباقة ${index + 1}` : `Package ${index + 1}`}
                            </span>
                            {editedPackages.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditedPackages(prev => prev.filter(p => p.id !== pkg.id))}
                                className="h-6 w-6 p-0 text-destructive"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                          <div className="grid gap-2 sm:grid-cols-4">
                            <Input
                              value={pkg.name}
                              onChange={(e) => setEditedPackages(prev => prev.map(p => 
                                p.id === pkg.id ? { ...p, name: e.target.value } : p
                              ))}
                              placeholder={isRTL ? "اسم الباقة" : "Package name"}
                              className="sm:col-span-2"
                            />
                            <Input
                              type="number"
                              value={pkg.duration_seconds}
                              onChange={(e) => setEditedPackages(prev => prev.map(p => 
                                p.id === pkg.id ? { ...p, duration_seconds: parseInt(e.target.value) || 0 } : p
                              ))}
                              placeholder={isRTL ? "ثواني" : "Seconds"}
                            />
                            <Input
                              type="number"
                              value={pkg.price || ""}
                              onChange={(e) => setEditedPackages(prev => prev.map(p => 
                                p.id === pkg.id ? { ...p, price: parseFloat(e.target.value) || 0 } : p
                              ))}
                              placeholder="SAR"
                            />
                          </div>
                          <Input
                            value={pkg.description}
                            onChange={(e) => setEditedPackages(prev => prev.map(p => 
                              p.id === pkg.id ? { ...p, description: e.target.value } : p
                            ))}
                            placeholder={isRTL ? "وصف الباقة" : "Description"}
                          />
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {profile.packages.length > 0 ? profile.packages.map((pkg, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div>
                          <p className="font-medium">{pkg.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {pkg.duration_seconds}s • {pkg.description || (isRTL ? "بدون وصف" : "No description")}
                          </p>
                        </div>
                        <Badge variant="secondary">{pkg.price.toLocaleString()} SAR</Badge>
                      </div>
                    )) : (
                      <p className="text-sm text-muted-foreground">{isRTL ? "لا توجد باقات" : "No packages"}</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bookings Tab */}
        <TabsContent value="bookings">
          <Card>
            <CardHeader>
              <CardTitle>{isRTL ? "طلبات الحجز" : "Booking Requests"}</CardTitle>
              <CardDescription>
                {isRTL ? "إدارة طلبات الحجز من العلامات التجارية" : "Manage booking requests from brands"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {bookings.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{isRTL ? "لا توجد طلبات حجز حتى الآن" : "No booking requests yet"}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {bookings.map((booking) => (
                    <Card key={booking.id} className="overflow-hidden">
                      <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row justify-between gap-4">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold">{booking.requester_name}</h4>
                              <Badge variant={
                                booking.status === 'pending' ? 'secondary' :
                                booking.status === 'accepted' ? 'default' :
                                booking.status === 'completed' ? 'outline' : 'destructive'
                              }>
                                {booking.status === 'pending' ? (isRTL ? "قيد الانتظار" : "Pending") :
                                 booking.status === 'accepted' ? (isRTL ? "مقبول" : "Accepted") :
                                 booking.status === 'completed' ? (isRTL ? "مكتمل" : "Completed") :
                                 booking.status === 'rejected' ? (isRTL ? "مرفوض" : "Rejected") :
                                 (isRTL ? "ملغى" : "Cancelled")}
                              </Badge>
                            </div>
                            
                            {booking.company_name && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Building2 className="h-4 w-4" />
                                {booking.company_name}
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Mail className="h-4 w-4" />
                              {booking.requester_email}
                            </div>
                            
                            {booking.requester_phone && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Phone className="h-4 w-4" />
                                {booking.requester_phone}
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2 text-sm">
                              <Badge variant="outline">{booking.campaign_type}</Badge>
                              {booking.budget && (
                                <span className="flex items-center gap-1">
                                  <DollarSign className="h-3 w-3" />
                                  {booking.budget.toLocaleString()} SAR
                                </span>
                              )}
                            </div>

                            {booking.preferred_start_date && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Clock className="h-4 w-4" />
                                {new Date(booking.preferred_start_date).toLocaleDateString()}
                                {booking.preferred_end_date && ` - ${new Date(booking.preferred_end_date).toLocaleDateString()}`}
                              </div>
                            )}

                            {booking.campaign_description && (
                              <p className="text-sm text-muted-foreground line-clamp-2">{booking.campaign_description}</p>
                            )}
                          </div>

                          <div className="flex sm:flex-col gap-2 justify-end">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setBookingDialog({ open: true, booking });
                                setBookingNotes(booking.influencer_notes || "");
                              }}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              {isRTL ? "عرض" : "View"}
                            </Button>
                            
                            {booking.status === 'pending' && (
                              <Button 
                                size="sm"
                                onClick={() => {
                                  setBookingDialog({ open: true, booking });
                                  setBookingNotes("");
                                }}
                              >
                                <MessageSquare className="h-4 w-4 mr-1" />
                                {isRTL ? "رد" : "Respond"}
                              </Button>
                            )}
                            
                            {booking.status === 'accepted' && (
                              <Button 
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setBookingDialog({ open: true, booking });
                                }}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                {isRTL ? "إكمال" : "Complete"}
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Booking Response Dialog */}
      <Dialog open={bookingDialog.open} onOpenChange={(open) => {
        setBookingDialog({ open, booking: open ? bookingDialog.booking : null });
        if (!open) setBookingNotes("");
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isRTL ? "تفاصيل طلب الحجز" : "Booking Request Details"}</DialogTitle>
            <DialogDescription>
              {bookingDialog.booking?.requester_name} - {bookingDialog.booking?.company_name || (isRTL ? "بدون شركة" : "No company")}
            </DialogDescription>
          </DialogHeader>

          {bookingDialog.booking && (
            <div className="space-y-4">
              <div className="grid gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{isRTL ? "نوع الحملة" : "Campaign Type"}</span>
                  <span>{bookingDialog.booking.campaign_type}</span>
                </div>
                {bookingDialog.booking.budget && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{isRTL ? "الميزانية" : "Budget"}</span>
                    <span>{bookingDialog.booking.budget.toLocaleString()} SAR</span>
                  </div>
                )}
                {bookingDialog.booking.preferred_start_date && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{isRTL ? "التاريخ" : "Dates"}</span>
                    <span>
                      {new Date(bookingDialog.booking.preferred_start_date).toLocaleDateString()}
                      {bookingDialog.booking.preferred_end_date && ` - ${new Date(bookingDialog.booking.preferred_end_date).toLocaleDateString()}`}
                    </span>
                  </div>
                )}
              </div>

              {bookingDialog.booking.campaign_description && (
                <div>
                  <Label className="text-muted-foreground">{isRTL ? "وصف الحملة" : "Campaign Description"}</Label>
                  <p className="mt-1 text-sm">{bookingDialog.booking.campaign_description}</p>
                </div>
              )}

              {bookingDialog.booking.status === 'pending' && (
                <>
                  <div className="space-y-2">
                    <Label>{isRTL ? "ملاحظاتك (اختياري)" : "Your Notes (optional)"}</Label>
                    <Textarea
                      value={bookingNotes}
                      onChange={(e) => setBookingNotes(e.target.value)}
                      placeholder={isRTL ? "أضف ملاحظاتك للعميل..." : "Add your notes for the client..."}
                      rows={3}
                    />
                  </div>

                  <DialogFooter className="gap-2">
                    <Button 
                      variant="destructive" 
                      onClick={() => handleUpdateBookingStatus('rejected')}
                      disabled={updatingBooking}
                    >
                      {updatingBooking ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4 mr-1" />}
                      {isRTL ? "رفض" : "Reject"}
                    </Button>
                    <Button 
                      onClick={() => handleUpdateBookingStatus('accepted')}
                      disabled={updatingBooking}
                    >
                      {updatingBooking ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                      {isRTL ? "قبول" : "Accept"}
                    </Button>
                  </DialogFooter>
                </>
              )}

              {bookingDialog.booking.status === 'accepted' && (
                <DialogFooter>
                  <Button 
                    onClick={() => handleUpdateBookingStatus('completed')}
                    disabled={updatingBooking}
                  >
                    {updatingBooking ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                    {isRTL ? "وضع علامة مكتمل" : "Mark as Completed"}
                  </Button>
                </DialogFooter>
              )}

              {bookingDialog.booking.status !== 'pending' && bookingDialog.booking.influencer_notes && (
                <div>
                  <Label className="text-muted-foreground">{isRTL ? "ملاحظاتك" : "Your Notes"}</Label>
                  <p className="mt-1 text-sm">{bookingDialog.booking.influencer_notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InfluencerDashboard;
